import React from 'react';
import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import type { LoaderFunction } from 'react-router-dom';
import type { MenuProps } from 'antd';

import {
  AppstoreOutlined,
  CalculatorOutlined,
  FileDoneOutlined,
  ShoppingCartOutlined,
  SendOutlined,
  RollbackOutlined,
  SyncOutlined,
  BookOutlined,
  DropboxOutlined,
  DatabaseOutlined,
  BuildOutlined,
  SettingOutlined,
  CloudUploadOutlined,
  ToolOutlined,
  SafetyOutlined,
  ApiOutlined,
  LinkOutlined,
  CodeOutlined,
  LineChartOutlined,
  RobotOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import PlaceholderPage from '@components/shared/PlaceholderPage';
import { BrandsList, BrandNew, BrandShow } from '@pages/admin/Brands';
import { BundlesList, BundlesShow, BundlesEdit } from '@pages/admin/Bundles';
import AdminBundleNew from '@pages/admin/Bundles/New';
import ConsumablesEdit from '@pages/admin/Consumables/Edit';
import ConsumablesNew from '@pages/admin/Consumables/New';
import ConsumablesList from '@pages/admin/Consumables/List';
import ConsumablesShow from '@pages/admin/Consumables/Show';
import ContactAddressEdit from '@pages/admin/ContactAddresses/Edit';
import ContactAddressNew from '@pages/admin/ContactAddresses/New';
import ContactAddressList from '@pages/admin/ContactAddresses/List';
import ContactAddressShow from '@pages/admin/ContactAddresses/Show';
import Dashboard from '@pages/admin/Dashboard';
import EcAccountsEdit from '@pages/admin/EcAccounts/Edit';
import EcAccountsNew from '@pages/admin/EcAccounts/New';
import EcAccountsShow from '@pages/admin/EcAccounts/Show';
import EcAccountsList from '@pages/admin/EcAccounts/List';
import { FlexibleProductsList, FlexibleProductsShow, FlexibleProductsEdit } from '@pages/admin/FlexibleProducts';
import FlexibleProductsNew from '@pages/admin/FlexibleProducts/New';
import ImportsList from '@pages/admin/Imports/List';
import ImportShow from '@pages/admin/Imports/Show';
import { ImportTemplatesList as AdminImportTemplatesList, ImportTemplateShow as AdminImportTemplateShow, ImportTemplateEdit as AdminImportTemplateEdit } from '@pages/admin/ImportTemplates';
import AdminImportTemplateNew from '@pages/admin/ImportTemplates/New';
import { List as InboundsList, Show as InboundsShow, Edit as InboundsEdit } from '@pages/admin/Inbounds';
import InboundsNew from '@pages/admin/Inbounds/New';
import { List as SuppliersList, Show as SuppliersShow, Edit as SuppliersEdit } from '@pages/admin/Suppliers';
import SuppliersNew from '@pages/admin/Suppliers/New';
import { List as PurchaseOrdersList, Show as PurchaseOrdersShow, Edit as PurchaseOrdersEdit } from '@pages/admin/PurchaseOrders';
import PurchaseOrdersNew from '@pages/admin/PurchaseOrders/New';
import AdminInventoriesList from '@pages/admin/Inventories/List';
import AdminInventoryOrders from '@pages/admin/Inventories/Orders';
import AdminInventoryMovementsList from '@pages/admin/InventoryMovements/List';
import AdminInventoryHoldsList from '@pages/admin/InventoryHolds/List';
import AdminInventoryHoldsNew from '@pages/admin/InventoryHolds/New';
import AdminInventoryHoldsShow from '@pages/admin/InventoryHolds/Show';
import AdminStockAdjustmentsList from '@pages/admin/StockAdjustments/List';
import AdminStockAdjustmentsShow from '@pages/admin/StockAdjustments/Show';
import InventoryMonitorsList from '@pages/admin/InventoryMonitors/List';
import InventoryMonitorsEdit from '@pages/admin/InventoryMonitors/Edit';
import InventoryMonitorsNew from '@pages/admin/InventoryMonitors/New';
import InventoryMonitorsShow from '@pages/admin/InventoryMonitors/Show';
import AdminNotificationList from '@pages/admin/Notifications/List';
import AdminNotificationShow from '@pages/admin/Notifications/Show';
import AdminOrderHoldsList from '@pages/admin/OrderHolds/List';
import { OrdersList, OrderNew, OrderShow, OrderEdit } from '@pages/admin/Orders';
import AdminOrderAnalytics from '@pages/admin/Orders/Analytics';
import SlaPerformance from '@pages/admin/Analytics/SlaPerformance';
import AdminFulfillmentMerge from '@pages/admin/Fulfillments/Merge';
import AdminUploadLabels from '@pages/admin/Orders/UploadLabels';
import AdminOrderSerialNumbersList from '@pages/admin/Orders/SerialNumbers/List';
import AdminOrderTemplateEdit from '@pages/admin/OrderTemplates/Edit';
import AdminOrderTemplateNew from '@pages/admin/OrderTemplates/New';
import AdminOrderTemplatesList from '@pages/admin/OrderTemplates/List';
import AdminOrderTemplateShow from '@pages/admin/OrderTemplates/Show';
import AdminOutOfStockProductsList from '@pages/admin/OutOfStockProducts/List';
import { List as OrderPostProcessorsList, Show as OrderPostProcessorsShow, Edit as OrderPostProcessorsEdit } from '@pages/admin/OrderPostProcessors';
import AdminOrderPostProcessorNew from '@pages/admin/OrderPostProcessors/New';
import AdminProductionBomEdit from '@pages/admin/ProductionBoms/Edit';
import AdminProductionBomsList from '@pages/admin/ProductionBoms/List';
import AdminProductionBomNew from '@pages/admin/ProductionBoms/New';
import AdminProductionBomShow from '@pages/admin/ProductionBoms/Show';
import AdminProductLabelTemplateEdit from '@pages/admin/ProductLabelTemplates/Edit';
import AdminProductLabelTemplateNew from '@pages/admin/ProductLabelTemplates/New';
import AdminProductLabelTemplatesList from '@pages/admin/ProductLabelTemplates/List';
import AdminProductLabelTemplateShow from '@pages/admin/ProductLabelTemplates/Show';
import { ProductsList, ProductEdit, ProductShow, ProductInventory, ReplaceSku } from '@pages/admin/Products';
import AdminProductNew from '@pages/admin/Products/New';
import AdminProductSerialNumbersList from '@pages/admin/ProductSerialNumbers/List';
import ReturnOrderEdit from '@pages/admin/ReturnOrders/Edit';
import ReturnOrderNew from '@pages/admin/ReturnOrders/New';
import ReturnOrdersList from '@pages/admin/ReturnOrders/List';
import ReturnOrderShow from '@pages/admin/ReturnOrders/Show';
import ReturnReceiptShow from '@pages/admin/ReturnReceipts/Show';
import ShelvesList from '@pages/admin/Shelves/List';
import ShelvesShow from '@pages/admin/Shelves/Show';
import AdminShippingAccountsList from '@pages/admin/ShippingAccounts/List';
import ShipmentsList from '@pages/admin/Shipments/List';
import AdminSalesChannelsEdit from '@pages/admin/SalesChannels/Edit';
import AdminSalesChannelsList from '@pages/admin/SalesChannels/List';
import AdminSalesChannelsNew from '@pages/admin/SalesChannels/New';
import AdminSalesChannelsShow from '@pages/admin/SalesChannels/Show';
import AdminMerchantInventoryTypesList from '@pages/admin/MerchantInventoryTypes/List';
import AdminInventoryAttributeDefinitionsList from '@pages/admin/InventoryAttributeDefinitions/List';
import AdminProductAttributeDefinitionsList from '@pages/admin/ProductAttributeDefinitions/List';
import { GeneralSettings } from '@pages/admin/Settings';
import AdminSluggishProductsIndex from '@pages/admin/SluggishProducts/Index';
import AdminInventorySnapshotsList from '@pages/admin/InventorySnapshots/List';
import AdminInventoryTransformationsList from '@pages/admin/InventoryTransformations/List';
import AdminInventoryTransformationsNew  from '@pages/admin/InventoryTransformations/New';
import AdminInventoryTransformationsShow from '@pages/admin/InventoryTransformations/Show';
import AdminInventoryTypeReclassifyRulesList from '@pages/admin/InventoryTypeReclassifyRules/List';
import AdminInventoryTypeReclassifyRulesForm from '@pages/admin/InventoryTypeReclassifyRules/Form';
import AdminUnknownProductsList from '@pages/admin/UnknownProducts/List';
import AdminUnknownProductShow from '@pages/admin/UnknownProducts/Show';
import AdminUsersList from '@pages/admin/Users/List';
import AdminUserEdit from '@pages/admin/Users/Edit';
import ProfileShow from '@pages/admin/Profile/Show';
import WorkflowsIndex from '@pages/admin/Workflows/Index';
import AdminAccessibleIpAddressesList from '@pages/admin/AccessibleIpAddresses/List';
import AdminIntegrationsList from '@pages/admin/Integrations/List';
import AdminIntegrationEdit from '@pages/admin/Integrations/Edit';
import AdminIntegrationNew from '@pages/admin/Integrations/New';
import AdminIntegrationShow from '@pages/admin/Integrations/Show';
import AdminWebhooksList from '@pages/admin/Webhooks/List';
import AdminWebhookEdit from '@pages/admin/Webhooks/Edit';
import AdminWebhookNew from '@pages/admin/Webhooks/New';
import AdminWebhookShow from '@pages/admin/Webhooks/Show';
import AdminWebhookDeliveriesList from '@pages/admin/WebhookDeliveries/List';
import AdminThirdPartyApiLogsList from '@pages/admin/ThirdPartyApiLogs/List';
import AdminThirdPartyApiLogsShow from '@pages/admin/ThirdPartyApiLogs/Show';
import AdminPartnerApiLogsList from '@pages/admin/PartnerApiLogs/List';
import AdminPartnerApiLogsShow from '@pages/admin/PartnerApiLogs/Show';

import AdminBillingsList from '@pages/admin/Billings/List';
import AdminBillingsShow from '@pages/admin/Billings/Show';
import {
  AiCenterOverview,
  AiCenterUsageRecords,
  AiCenterPaymentMethods,
  AiCenterInvoices,
  AiCenterBillingProfile
} from '@pages/admin/AiCenter';
import adminApiFetch from '@services/admin/apiService';

// placeholder 只在沒有 component 時提供預設值
// 使用方式: { ...placeholder('title'), component: MyComponent } - component 會覆蓋 placeholder
const placeholder = (titleKey: string) => ({
  component: PlaceholderPage,
  componentProps: { titleKey, role: 'admin' as const },
});

// ==================== 型別定義 ====================

// 'resource:action' literal — 與 <Can> / <CanShow> / generated Permission 一致。
// TODO: 收緊為 `Permission` 一旦 config/permissions.yml 涵蓋所有 menu 用到的 scope。
export type RequiredPermission = string;

interface MenuItemBase {
  key: string;
  labelKey: string;
  icon?: ComponentType<any>;
  isParent?: boolean;
  requiredPermission?: RequiredPermission;
  visibilityCheck?: (merchant: Record<string, unknown> | null | undefined) => boolean;
  // 從側選單隱藏，但保留路由（仍可由直接 URL / 內部連結進入）。
  hiddenInMenu?: boolean;
  children?: MenuItem[];
}

interface InternalMenuItem extends MenuItemBase {
  external?: false;
  path?: string;
  component?: ComponentType<any>;
  componentProps?: Record<string, unknown>;
}

interface ExternalMenuItem extends MenuItemBase {
  external: true;
  url: string;
  target?: string;
}

export type MenuItem = InternalMenuItem | ExternalMenuItem;

export interface RouteEntry {
  key?: string;
  path: string;
  component: ComponentType<any>;
  componentProps?: Record<string, unknown>;
  loader?: LoaderFunction;
  requiredPermission?: RequiredPermission;
  visibilityCheck?: MenuItem['visibilityCheck'];
}

export interface MenuMetaEntry {
  path?: string;
  parentKey?: string;
  labelKey?: string;
  external?: boolean;
  url?: string;
  target?: string;
}

interface VisibilityContext {
  permissions?: readonly string[];
  merchant?: Record<string, unknown> | null;
}

type TFunction = (key: string) => string;

type AntdMenuItems = NonNullable<MenuProps['items']>;
type AntdMenuItem = AntdMenuItems[number] & { children?: AntdMenuItems };

const hasInternalPath = (item: MenuItem): item is InternalMenuItem & { path: string } =>
  !item.external && typeof (item as InternalMenuItem).path === 'string';

const hasInternalRoute = (
  item: MenuItem,
): item is InternalMenuItem & { path: string; component: ComponentType<any> } =>
  hasInternalPath(item) && typeof (item as InternalMenuItem).component === 'function';

export type GateReason = 'permission' | 'visibility';
export type GateResult = { ok: true } | { ok: false; reason: GateReason };

type GateItem = { requiredPermission?: RequiredPermission; visibilityCheck?: MenuItem['visibilityCheck']; hiddenInMenu?: boolean };

/**
 * Permission-only gate: only inspects `requiredPermission`. Used by the outer
 * <PermissionGate> in adminRouter so a user denied for RBAC reasons sees
 * "no access" rather than "feature not enabled for merchant".
 */
export function getPermissionGateResult(
  item: GateItem,
  { permissions = [] }: VisibilityContext = {},
): GateResult {
  if (item.requiredPermission) {
    const [resource, action] = item.requiredPermission.split(':');
    const has = permissions.includes('*:*') ||
                permissions.includes(`${resource}:*`) ||
                permissions.includes(`*:${action}`) ||
                permissions.includes(item.requiredPermission);
    if (!has) return { ok: false, reason: 'permission' };
  }
  return { ok: true };
}

/**
 * Merchant-visibility gate: ignores `requiredPermission`. Used by the inner
 * <VisibilityGate>; only routes whose permission gate already passed reach it.
 */
export function getVisibilityGateResult(
  item: GateItem,
  { merchant = null }: VisibilityContext = {},
): GateResult {
  if (item.visibilityCheck && merchant) {
    if (!item.visibilityCheck(merchant)) return { ok: false, reason: 'visibility' };
  }
  return { ok: true };
}

/**
 * Decide whether an admin menu/route entry is visible.
 * 統一被 menu rendering（AdminLayout / UserMenuContext）使用。
 * Route gating（adminRouter）改為直接消費 getPermissionGateResult /
 * getVisibilityGateResult 兩個 predicate，以便分別 toast 對應的訊息。
 */
export function isMenuItemVisible(
  item: GateItem,
  ctx: VisibilityContext = {},
): boolean {
  if (item.hiddenInMenu) return false;
  return getPermissionGateResult(item, ctx).ok && getVisibilityGateResult(item, ctx).ok;
}

export const adminMenuConfig: MenuItem[] = [
  // ==================== 商品管理 ====================
  {
    key: 'products',
    icon: AppstoreOutlined,
    labelKey: 'admin.menu.products.title',
    isParent: true,
    requiredPermission: 'products:read',
    children: [
      {
        key: 'products.list',
        labelKey: 'admin.menu.products.list',
        path: '/products',
        component: ProductsList,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.bundles',
        labelKey: 'admin.menu.products.bundles',
        path: '/bundles',
        component: BundlesList,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.flexible',
        labelKey: 'admin.menu.products.flexible',
        path: '/flexible_products',
        component: FlexibleProductsList,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.brands',
        labelKey: 'admin.menu.products.brands',
        path: '/brands',
        component: BrandsList,
        requiredPermission: 'brands:read',
      },
      {
        key: 'products.unknown',
        labelKey: 'admin.menu.products.unknown',
        path: '/unknown_products',
        component: AdminUnknownProductsList,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.sluggish',
        labelKey: 'admin.menu.products.sluggish',
        path: '/sluggish_products',
        component: AdminSluggishProductsIndex,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.label_templates',
        labelKey: 'admin.menu.products.label_templates',
        path: '/product_label_templates',
        component: AdminProductLabelTemplatesList,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.serial_numbers',
        labelKey: 'admin.menu.products.serial_numbers',
        path: '/product_serial_numbers',
        component: AdminProductSerialNumbersList,
        requiredPermission: 'products:read',
      },
      {
        key: 'products.replace_sku',
        labelKey: 'admin.menu.products.replace_sku',
        path: '/products/replace-sku',
        requiredPermission: 'products:write',
        component: ReplaceSku,
      },
    ],
  },

  // ==================== 庫存管理 ====================
  {
    key: 'stocks',
    icon: DatabaseOutlined,
    labelKey: 'admin.menu.stocks.title',
    isParent: true,
    requiredPermission: 'inventories:read',
    children: [
      {
        key: 'stocks.current',
        labelKey: 'admin.menu.stocks.current',
        path: '/stocks',
        component: AdminInventoriesList,
        requiredPermission: 'inventories:read',
      },
      {
        key: 'stocks.history',
        labelKey: 'admin.menu.stocks.history',
        path: '/inventory_snapshots',
        component: AdminInventorySnapshotsList,
        requiredPermission: 'inventories:read',
      },
      {
        key: 'stocks.adjustments',
        labelKey: 'admin.menu.stocks.adjustments',
        path: '/stock_adjustments',
        component: AdminStockAdjustmentsList,
        requiredPermission: 'stock_adjustments:read',
      },
      {
        key: 'stocks.inventory_movements',
        labelKey: 'admin.menu.stocks.inventory_movements',
        path: '/inventory_movements',
        component: AdminInventoryMovementsList,
        requiredPermission: 'inventories:read',
      },
      {
        key: 'stocks.inventory_holds',
        labelKey: 'admin.menu.stocks.inventory_holds',
        path: '/inventory_holds',
        component: AdminInventoryHoldsList,
        requiredPermission: 'inventory_holds:read',
      },
      {
        key: 'stocks.inventory_transformations',
        labelKey: 'admin.menu.stocks.inventory_transformations',
        path: '/inventory_transformations',
        component: AdminInventoryTransformationsList,
        requiredPermission: 'inventories:read',
      },
      {
        key: 'stocks.inventory_type_reclassify_rules',
        labelKey: 'admin.menu.stocks.inventory_type_reclassify_rules',
        path: '/inventory_type_reclassify_rules',
        component: AdminInventoryTypeReclassifyRulesList,
        requiredPermission: 'inventories:read',
      },
      {
        key: 'stocks.trackers',
        labelKey: 'admin.menu.stocks.trackers',
        path: '/inventory_monitors',
        component: InventoryMonitorsList, // Use the actual component
        requiredPermission: 'inventories:read',
      },
    ],
  },

  // ==================== 採購管理 ====================
  {
    key: 'purchase_orders',
    icon: AuditOutlined,
    labelKey: 'admin.menu.purchase_orders.title',
    isParent: true,
    requiredPermission: 'purchase_orders:read',
    children: [
      {
        key: 'purchase_orders.list',
        labelKey: 'admin.menu.purchase_orders.list',
        path: '/purchase_orders',
        component: PurchaseOrdersList,
        requiredPermission: 'purchase_orders:read',
      },
      {
        key: 'purchase_orders.suppliers',
        labelKey: 'admin.menu.suppliers.list',
        path: '/suppliers',
        component: SuppliersList,
        requiredPermission: 'suppliers:read',
      },
    ],
  },

  // ==================== 入庫管理 ====================
  {
    key: 'inbounds',
    icon: FileDoneOutlined,
    labelKey: 'admin.menu.inbounds.title',
    isParent: true,
    requiredPermission: 'inbounds:read',
    children: [
      {
        key: 'inbounds.list',
        labelKey: 'admin.menu.inbounds.list',
        path: '/inbounds',
        component: InboundsList,
        requiredPermission: 'inbounds:read',
      },
    ],
  },


  // ==================== 訂單管理（出庫）====================
  {
    key: 'orders',
    icon: ShoppingCartOutlined,
    labelKey: 'admin.menu.orders.title',
    isParent: true,
    requiredPermission: 'orders:read',
    children: [
      {
        key: 'orders.list',
        labelKey: 'admin.menu.orders.list',
        path: '/orders',
        component: OrdersList,
        requiredPermission: 'orders:read',
      },
      {
        key: 'orders.order_holds',
        labelKey: 'admin.menu.orders.order_holds',
        path: '/order_holds',
        component: AdminOrderHoldsList,
        requiredPermission: 'orders:read',
      },
      {
        key: 'orders.out_of_stock',
        labelKey: 'admin.menu.orders.out_of_stock',
        path: '/orders/out_of_stocks',
        component: AdminOutOfStockProductsList,
        requiredPermission: 'orders:read',
      },
      {
        key: 'orders.templates',
        labelKey: 'admin.menu.orders.templates',
        path: '/order_templates',
        component: AdminOrderTemplatesList,
        requiredPermission: 'orders:read',
      },
      {
        key: 'fulfillments.merge',
        labelKey: 'admin.menu.fulfillments.merge',
        path: '/fulfillments/merge',
        component: AdminFulfillmentMerge,
        requiredPermission: 'orders:write',
      },

      {
        key: 'orders.serial_numbers',
        labelKey: 'admin.menu.orders.serial_numbers',
        path: '/orders/serial_numbers',
        component: AdminOrderSerialNumbersList,
        requiredPermission: 'orders:read',
      },
      {
        key: 'orders.post_processors',
        labelKey: 'admin.menu.orders.post_processors',
        path: '/order_post_processors',
        requiredPermission: 'orders:read',
        component: OrderPostProcessorsList,
        // 暫時從側選單隱藏；路由保留，功能未移除。
        hiddenInMenu: true,
      },
    ],
  },

  // ==================== 退貨管理 ====================
  {
    key: 'returns',
    icon: RollbackOutlined,
    labelKey: 'admin.menu.returns.title',
    isParent: true,
    requiredPermission: 'orders:read',
    children: [
      {
        key: 'returns.list',
        labelKey: 'admin.menu.returns.list',
        path: '/return_orders',
        component: ReturnOrdersList,
        requiredPermission: 'orders:read',
      },
    ],
  },

  // ==================== 托運單管理 ====================
  {
    key: 'shipments',
    icon: SendOutlined,
    labelKey: 'admin.menu.shipments.title',
    isParent: true,
    requiredPermission: 'orders:read',
    children: [
      {
        key: 'shipments.list',
        labelKey: 'admin.menu.shipments.list',
        path: '/shipments',
        component: ShipmentsList,
        requiredPermission: 'orders:read',
      },
      {
        key: 'shipments.upload_labels',
        labelKey: 'admin.menu.shipments.upload_labels',
        path: '/shipments/upload_labels',
        requiredPermission: 'orders:write',
        component: AdminUploadLabels,
      },
    ],
  },

  // ==================== 圖表分析 ====================
  {
    key: 'analytics',
    icon: LineChartOutlined,
    labelKey: 'admin.menu.analytics.title',
    isParent: true,
    requiredPermission: 'orders:read',
    children: [
      {
        key: 'analytics.orders',
        labelKey: 'admin.menu.orders.analytics',
        path: '/analytics/orders',
        component: AdminOrderAnalytics,
        requiredPermission: 'orders:read',
      },
      {
        key: 'analytics.sla',
        labelKey: 'admin.menu.analytics.sla',
        path: '/analytics/sla',
        component: SlaPerformance,
        requiredPermission: 'orders:read',
      },
    ],
  },

  // ==================== 資料管理 ====================
  {
    key: 'data',
    icon: BookOutlined,
    labelKey: 'admin.menu.data.title',
    isParent: true,
    children: [
      {
        key: 'data.contact_addresses',
        labelKey: 'admin.menu.contact_addresses.title',
        path: '/contact_addresses',
        component: ContactAddressList,
        requiredPermission: 'contact_addresses:read',
      },
      {
        key: 'data.consumables',
        labelKey: 'admin.menu.consumables.title',
        path: '/consumables',
        component: ConsumablesList,
        requiredPermission: 'consumables:read',
      },
      {
        key: 'data.shelves',
        labelKey: 'admin.menu.shelves.title',
        path: '/shelves',
        component: ShelvesList,
        requiredPermission: 'shelves:read',
        visibilityCheck: (merchant) => merchant?.shelves_visible !== false,
      },
      {
        key: 'data.inventory_types',
        labelKey: 'admin.menu.data.inventory_types',
        path: '/merchant_inventory_types',
        component: AdminMerchantInventoryTypesList,
        requiredPermission: 'merchant_inventory_types:read',
      },
      {
        key: 'data.sales_channels',
        labelKey: 'admin.menu.data.sales_channels',
        path: '/sales_channels',
        component: AdminSalesChannelsList,
        requiredPermission: 'merchants:read',
      },
      {
        key: 'data.inventory_attribute_definitions',
        labelKey: 'admin.menu.data.inventory_attribute_definitions',
        path: '/inventory_attribute_definitions',
        component: AdminInventoryAttributeDefinitionsList,
        requiredPermission: 'merchants:write',
      },
      {
        key: 'data.product_attribute_definitions',
        labelKey: 'admin.menu.data.product_attribute_definitions',
        path: '/product_attribute_definitions',
        component: AdminProductAttributeDefinitionsList,
        requiredPermission: 'merchants:write',
      },
      {
        key: 'data.imports',
        labelKey: 'admin.menu.imports.title',
        path: '/imports',
        component: ImportsList,
        requiredPermission: 'imports:read',
      },
      {
        key: 'data.import_templates',
        labelKey: 'admin.menu.import_templates.title',
        path: '/import_templates',
        component: AdminImportTemplatesList,
        requiredPermission: 'merchants:write',
      },
    ],
  },

  // ==================== 平台串接 ====================
  {
    key: 'platform',
    icon: LinkOutlined,
    labelKey: 'admin.menu.platform.title',
    isParent: true,
    requiredPermission: 'ec_accounts:read',
    children: [
      {
        key: 'platform.ec_accounts',
        labelKey: 'admin.menu.platform.ec_accounts',
        path: '/ec_accounts',
        component: EcAccountsList,
        requiredPermission: 'ec_accounts:read',
      },
      {
        key: 'platform.shipping_accounts',
        labelKey: 'admin.menu.platform.shipping_accounts',
        path: '/shipping_accounts',
        component: AdminShippingAccountsList,
        requiredPermission: 'shipping_accounts:read',
      },
      {
        key: 'platform.api_logs',
        labelKey: 'admin.menu.platform.third_party_api_logs',
        path: '/third_party_api_logs',
        component: AdminThirdPartyApiLogsList,
        requiredPermission: 'logs:read',
      },
    ],
  },

  // ==================== 開發者工具 ====================
  {
    key: 'developer_tools',
    icon: CodeOutlined,
    labelKey: 'admin.menu.developer_tools.title',
    isParent: true,
    requiredPermission: 'integrations:read',
    children: [
      {
        key: 'developer_tools.integrations',
        labelKey: 'admin.menu.developer_tools.integrations',
        path: '/integrations',
        component: AdminIntegrationsList,
        requiredPermission: 'integrations:read',
      },
      {
        key: 'developer_tools.webhook_deliveries',
        labelKey: 'admin.menu.developer_tools.webhook_deliveries',
        path: '/webhook_deliveries',
        component: AdminWebhookDeliveriesList,
        requiredPermission: 'logs:read',
      },
      {
        key: 'developer_tools.partner_api_logs',
        labelKey: 'admin.menu.developer_tools.partner_api_logs',
        path: '/partner_api_logs',
        component: AdminPartnerApiLogsList,
        requiredPermission: 'logs:read',
      },
      {
        key: 'developer_tools.api_docs',
        labelKey: 'admin.menu.developer_tools.api_docs',
        external: true,
        url: '/partners/docs',
        target: '_blank',
      },
    ],
  },

  // ==================== 自訂工具 ====================
  {
    key: 'workflows',
    icon: ToolOutlined,
    labelKey: 'admin.menu.workflows',
    path: '/workflows',
    component: WorkflowsIndex,
    // requiredPermission: 'workflows:read',  // TODO: 加入權限後啟用
  },

  // ==================== 帳務管理 ====================
  {
    key: 'billings',
    icon: CalculatorOutlined,
    labelKey: 'admin.menu.billings',
    path: '/billings',
    component: AdminBillingsList,
    requiredPermission: 'billings:read',
  },

  // ==================== AI 中心 ====================
  {
    key: 'ai_center',
    icon: RobotOutlined,
    labelKey: 'admin.menu.ai_center.title',
    isParent: true,
    requiredPermission: 'governance:read',
    children: [
      {
        key: 'ai_center.overview',
        labelKey: 'admin.menu.ai_center.overview',
        path: '/ai_center',
        component: AiCenterOverview,
      },
      {
        key: 'ai_center.usage_records',
        labelKey: 'admin.menu.ai_center.usage_records',
        path: '/ai_center/usage_records',
        component: AiCenterUsageRecords,
      },
      {
        key: 'ai_center.payment_methods',
        labelKey: 'admin.menu.ai_center.payment_methods',
        path: '/ai_center/payment_methods',
        component: AiCenterPaymentMethods,
      },
      {
        key: 'ai_center.invoices',
        labelKey: 'admin.menu.ai_center.invoices',
        path: '/ai_center/invoices',
        component: AiCenterInvoices,
      },
      {
        key: 'ai_center.billing_profile',
        labelKey: 'admin.menu.ai_center.billing_profile',
        path: '/ai_center/billing_profile',
        component: AiCenterBillingProfile,
      },
    ],
  },

  // ==================== 設定 ====================
  {
    key: 'settings',
    icon: SettingOutlined,
    labelKey: 'admin.menu.settings.title',
    isParent: true,
    children: [
      {
        key: 'settings.general',
        labelKey: 'admin.menu.settings.general',
        path: '/settings',
        component: GeneralSettings,
        requiredPermission: 'merchants:write',
      },
      {
        key: 'settings.users',
        labelKey: 'admin.menu.settings.users',
        path: '/users',
        component: AdminUsersList,
        requiredPermission: 'users:read',
      },
      {
        key: 'settings.accessible_ip_addresses',
        labelKey: 'admin.menu.settings.accessible_ip_addresses',
        path: '/accessible_ip_addresses',
        component: AdminAccessibleIpAddressesList,
        requiredPermission: 'accessible_ip_addresses:read',
      },
    ],
  },

];

// ==================== Route Loaders ====================
// 通用 loader 函數
// 注意：rootLoader 已確保 token 存在，這裡可以安全地使用 adminApiFetch
// 失敗時拋出 Response，讓 React Router 的 isRouteErrorResponse 正確識別 status code，
// 由 RouteErrorBoundary 渲染對應的 404/403 等頁面。
const createLoader = (resource: string): LoaderFunction => async ({ params }) => {
  const { id } = params;
  try {
    const response = await adminApiFetch(`${resource}/${id}`);
    if (!response.ok) {
      throw new Response(response.statusText || `Failed to fetch ${resource}/${id}`, { status: response.status });
    }
    const data = await response.json();
    // envelope 失敗也包成 Response，讓 isRouteErrorResponse 能正確抓到 status
    if (!data.success) {
      throw new Response(data.message || `Failed to load ${resource}`, { status: 500 });
    }
    return data.data;
  } catch (error) {
    console.error(`[Loader Error] ${resource}/${id}:`, error);
    if (error instanceof Response) throw error;
    const status = (error as { status?: number } | null)?.status;
    if (status) {
      throw new Response((error as { message?: string }).message || '', { status });
    }
    throw error;
  }
};

// Detail routes (with :id parameter) - Must be defined BEFORE list routes
const adminDetailRoutes: RouteEntry[] = [
  // Profile (current user)
  { path: '/profile', component: ProfileShow },

  // API Logs detail
  { path: '/third_party_api_logs/:id', component: AdminThirdPartyApiLogsShow, loader: createLoader('third_party_api_logs') },
  { path: '/partner_api_logs/:id', component: AdminPartnerApiLogsShow, loader: createLoader('partner_api_logs') },

  // Import detail
  { path: '/imports/:id', component: ImportShow, loader: createLoader('imports') },

  // User detail
  { path: '/users/:id/edit', component: AdminUserEdit, loader: createLoader('users') },

  // Brand routes
  { path: '/brands/new', component: BrandNew },
  { path: '/brands/:id', component: BrandShow, loader: createLoader('brands') },

  // Order routes
  { path: '/orders/new', component: OrderNew },
  { path: '/orders/:id/edit', component: OrderEdit },
  { path: '/orders/:id', component: OrderShow, loader: createLoader('orders') },

  // Order Post Processor routes
  { path: '/order_post_processors/new', component: AdminOrderPostProcessorNew },
  { path: '/order_post_processors/:id/edit', component: OrderPostProcessorsEdit, loader: createLoader('order_post_processors') },
  { path: '/order_post_processors/:id', component: OrderPostProcessorsShow, loader: createLoader('order_post_processors') },

  // Product routes
  { path: '/products/new', component: AdminProductNew },
  { path: '/products/:id/edit', component: ProductEdit },
  { path: '/products/:id/inventory', component: ProductInventory, loader: createLoader('products') },
  { path: '/products/:id', component: ProductShow, loader: createLoader('products') },

  // Unknown product routes
  { path: '/unknown_products/:id', component: AdminUnknownProductShow, loader: createLoader('unknown_products') },

  // Inventory routes
  {
    path: '/inventories/:id/orders', component: AdminInventoryOrders, loader: async ({ params }) => {
      const response = await adminApiFetch(`inventories/${params.id}/orders`);
      if (!response.ok) throw new Error('Failed to fetch inventory orders');
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Failed to load inventory orders');
      return data.data;
    }
  },

  // Bundle routes
  { path: '/bundles/new', component: AdminBundleNew },
  { path: '/bundles/:id/edit', component: BundlesEdit },
  { path: '/bundles/:id', component: BundlesShow, loader: createLoader('bundles') },

  // Flexible product routes
  { path: '/flexible_products/new', component: FlexibleProductsNew },
  { path: '/flexible_products/:id/edit', component: FlexibleProductsEdit },
  { path: '/flexible_products/:id', component: FlexibleProductsShow, loader: createLoader('flexible_products') },

  // Process BOM routes
  { path: '/production_boms/new', component: AdminProductionBomNew },
  { path: '/production_boms/:id/edit', component: AdminProductionBomEdit },
  { path: '/production_boms/:id', component: AdminProductionBomShow, loader: createLoader('production_boms') },

  // Contact address routes
  { path: '/contact_addresses/new', component: ContactAddressNew },
  { path: '/contact_addresses/:id/edit', component: ContactAddressEdit },
  { path: '/contact_addresses/:id', component: ContactAddressShow, loader: createLoader('contact_addresses') },

  // Consumable routes
  { path: '/consumables/new', component: ConsumablesNew },
  { path: '/consumables/:id/edit', component: ConsumablesEdit },
  { path: '/consumables/:id', component: ConsumablesShow, loader: createLoader('consumables') },

  // Supplier routes
  { path: '/suppliers/new', component: SuppliersNew },
  { path: '/suppliers/:id/edit', component: SuppliersEdit },
  { path: '/suppliers/:id', component: SuppliersShow, loader: createLoader('suppliers') },

  // Purchase Order routes
  { path: '/purchase_orders/new', component: PurchaseOrdersNew },
  { path: '/purchase_orders/:id/edit', component: PurchaseOrdersEdit },
  { path: '/purchase_orders/:id', component: PurchaseOrdersShow, loader: createLoader('purchase_orders') },

  // Inbound routes
  { path: '/inbounds/new', component: InboundsNew },
  { path: '/inbounds/:id/edit', component: InboundsEdit },
  { path: '/inbounds/:id', component: InboundsShow, loader: createLoader('inbounds') },

  // Inventory Hold routes
  { path: '/inventory_holds/new', component: AdminInventoryHoldsNew },
  { path: '/inventory_holds/:id', component: AdminInventoryHoldsShow, loader: createLoader('inventory_holds') },

  // Inventory Transformation routes
  { path: '/inventory_transformations/new', component: AdminInventoryTransformationsNew },
  { path: '/inventory_transformations/:id', component: AdminInventoryTransformationsShow, loader: createLoader('inventory_transformations') },

  // Inventory Type Reclassify Rule routes
  { path: '/inventory_type_reclassify_rules/new', component: AdminInventoryTypeReclassifyRulesForm },
  { path: '/inventory_type_reclassify_rules/:id', component: AdminInventoryTypeReclassifyRulesForm },

  // Inventory Monitor routes
  { path: '/inventory_monitors/new', component: InventoryMonitorsNew },
  { path: '/inventory_monitors/:id/edit', component: InventoryMonitorsEdit },
  { path: '/inventory_monitors/:id', component: InventoryMonitorsShow, loader: createLoader('inventory_monitors') },

  // Stock Adjustment detail (read-only — admin can drill into a single adjustment)
  { path: '/stock_adjustments/:id', component: AdminStockAdjustmentsShow, loader: createLoader('stock_adjustments') },

  // Return order routes
  { path: '/return_orders/new', component: ReturnOrderNew },
  { path: '/return_orders/:id/edit', component: ReturnOrderEdit },
  { path: '/return_orders/:id', component: ReturnOrderShow, loader: createLoader('return_orders') },

  // Return receipt detail (read-only — admin can drill into a single receipt
  // from the RO show page for debugging stuck / :in_progress ones).
  { path: '/return_receipts/:id', component: ReturnReceiptShow, loader: createLoader('return_receipts') },

  // Shelf detail
  { path: '/shelves/:id', component: ShelvesShow, loader: createLoader('shelves') },

  // Order Template routes
  { path: '/order_templates/new', component: AdminOrderTemplateNew },
  { path: '/order_templates/:id/edit', component: AdminOrderTemplateEdit, loader: createLoader('order_templates') },
  { path: '/order_templates/:id', component: AdminOrderTemplateShow, loader: createLoader('order_templates') },

  // Product Label Template routes
  { path: '/product_label_templates/new', component: AdminProductLabelTemplateNew },
  { path: '/product_label_templates/:id/edit', component: AdminProductLabelTemplateEdit, loader: createLoader('product_label_templates') },
  { path: '/product_label_templates/:id', component: AdminProductLabelTemplateShow, loader: createLoader('product_label_templates') },

  // EC Account routes
  { path: '/ec_accounts/new', component: EcAccountsNew },
  { path: '/ec_accounts/:id/edit', component: EcAccountsEdit },
  { path: '/ec_accounts/:id', component: EcAccountsShow },

  // Merchant Channel routes
  { path: '/sales_channels/new', component: AdminSalesChannelsNew },
  { path: '/sales_channels/:id/edit', component: AdminSalesChannelsEdit, loader: createLoader('sales_channels') },
  { path: '/sales_channels/:id', component: AdminSalesChannelsShow, loader: createLoader('sales_channels') },

  // Notification routes
  { path: '/notifications/:id', component: AdminNotificationShow, loader: createLoader('notifications') },

  // Billing routes
  { path: '/billings/:id', component: AdminBillingsShow, loader: createLoader('merchant_billings') },

  // Integration routes
  { path: '/integrations/new', component: AdminIntegrationNew },
  { path: '/integrations/:id/edit', component: AdminIntegrationEdit, loader: createLoader('integrations') },
  { path: '/integrations/:id', component: AdminIntegrationShow, loader: createLoader('integrations') },

  // Webhook routes
  { path: '/webhooks/new', component: AdminWebhookNew },
  { path: '/webhooks/:id/edit', component: AdminWebhookEdit, loader: createLoader('webhooks') },
  { path: '/webhooks/:id', component: AdminWebhookShow, loader: createLoader('webhooks') },

  // Import Template routes
  { path: '/import_templates/new', component: AdminImportTemplateNew },
  { path: '/import_templates/:id/edit', component: AdminImportTemplateEdit, loader: createLoader('import_templates') },
  { path: '/import_templates/:id', component: AdminImportTemplateShow, loader: createLoader('import_templates') },
];

// List routes and other non-parameterized routes
const adminListRoutes: RouteEntry[] = [
  { path: '/', component: Dashboard },
  { path: '/dashboard', component: Dashboard },
  { path: '/users', component: AdminUsersList },
  { path: '/notifications', component: AdminNotificationList },
];

// Merge detail routes FIRST, then list routes
// This ensures detail routes are matched before list routes
export const adminAdditionalRoutes: RouteEntry[] = [
  ...adminDetailRoutes,
  ...adminListRoutes,
];

// 精瘦版：只保留 menu rendering / breadcrumb 實際讀的欄位
const buildMenuMeta = (
  items: MenuItem[],
  parentKey?: string,
  acc: Record<string, MenuMetaEntry> = {},
): Record<string, MenuMetaEntry> => {
  items.forEach((item) => {
    if (item.external) {
      acc[item.key] = {
        external: true,
        url: item.url,
        target: item.target,
        labelKey: item.labelKey,
        ...(parentKey ? { parentKey } : {}),
      };
    } else {
      acc[item.key] = {
        path: item.path,
        external: false,
        labelKey: item.labelKey,
        ...(parentKey ? { parentKey } : {}),
      };
    }

    if (item.children) {
      buildMenuMeta(item.children, item.key, acc);
    }
  });

  return acc;
};

// 只 map leaf（與 operator 對齊）— parent 通常無 path 或會被 child 同 path 覆蓋造成不一致
const buildPathToKeyMap = (items: MenuItem[]): Record<string, string> => {
  const map: Record<string, string> = {};

  const walk = (nodes: MenuItem[]) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        walk(node.children);
      } else if (hasInternalPath(node)) {
        map[node.path] = node.key;
      }
    });
  };

  walk(items);
  return map;
};

const collectRoutes = (items: MenuItem[], routes: RouteEntry[] = []): RouteEntry[] => {
  items.forEach((item) => {
    if (hasInternalRoute(item)) {
      routes.push({
        key: item.key,
        path: item.path,
        component: item.component,
        componentProps: item.componentProps,
        requiredPermission: item.requiredPermission,
        visibilityCheck: item.visibilityCheck,
      });
    }

    if (item.children) {
      collectRoutes(item.children, routes);
    }
  });

  return routes;
};

// Leaf 的 label 包成 <Link> / <a>，讓 Cmd+Click / middle-click / 右鍵「在新分頁開啟」全部可用。
// Parent（有 children）本身不導航，label 維持純文字。
export const renderMenuItemLabel = (item: MenuItem, t: TFunction): React.ReactNode => {
  const text = t(item.labelKey);
  if (item.children) return text;
  if (item.external) {
    return React.createElement(
      'a',
      {
        href: item.url,
        target: item.target || '_blank',
        rel: 'noopener noreferrer',
        className: 'admin-menu__link',
      },
      text,
    );
  }
  if (hasInternalPath(item)) {
    return React.createElement(
      Link,
      { to: item.path, className: 'admin-menu__link' },
      text,
    );
  }
  return text;
};

// 權限過濾已由 isMenuItemVisible 在 caller 端做完；此函式只負責把 MenuItem 轉成 antd 形狀。
// merchant 仍保留，因為某些 item 的 visibilityCheck 需要 — 但目前已收進 isMenuItemVisible，
// 所以 caller 可以也透過 isMenuItemVisible 先過濾掉 invisible items。
const toAntdMenuItems = (items: MenuItem[], t: TFunction): AntdMenuItems =>
  items
    .map((item) => {
      const menuItem: AntdMenuItem = {
        key: item.key,
        icon: item.icon ? React.createElement(item.icon) : undefined,
        label: renderMenuItemLabel(item, t),
      };

      if (item.children) {
        menuItem.children = toAntdMenuItems(item.children, t);
      }

      return menuItem;
    })
    .filter((item) => {
      // parent 若沒有任何可見子選單，整個隱藏
      const children = (item as AntdMenuItem).children;
      if (children) return children.length > 0;
      return true;
    });

export const adminMenuMeta: Record<string, MenuMetaEntry> = buildMenuMeta(adminMenuConfig);
export const adminPathToKeyMap: Record<string, string> = buildPathToKeyMap(adminMenuConfig);
export const adminMenuRoutes: RouteEntry[] = collectRoutes(adminMenuConfig);

// 去重：手動定義的路由優先（保留 loader 等 metadata），自動產生的補位
export const adminRoutes: RouteEntry[] = [
  ...adminAdditionalRoutes,
  ...adminMenuRoutes.filter(
    (mr) => !adminAdditionalRoutes.some((ar) => ar.path === mr.path),
  ),
];

export const createMenuItems = (
  t: TFunction,
  menuConfig: MenuItem[] = adminMenuConfig,
): AntdMenuItems => toAntdMenuItems(menuConfig, t);
